import { useQuery } from '@tanstack/react-query';
import { configurationService, FeatureFlag } from 'commons/services';
import { useFeatureFlag } from './useFeatureFlag';
import { useIdentityValidation } from './useIdentityValidation';

type UseProofOfLifeUrlOptions = {
  /**
   * Only fetch when the proof of life url is actually needed. Important while
   * the dedicated endpoint is gated by FS_COMMONS_PROOF_OF_LIFE_URL_ENDPOINT_ENABLED,
   * because GET /admin/kyc/proof-of-life fails (500) when the url is empty.
   */
  enabled?: boolean;
};

/**
 * Single source of truth for the proof of life url during the rollout of the
 * dedicated commons endpoint.
 *
 * - Flag ON  → GET /admin/kyc/proof-of-life (new endpoint).
 * - Flag OFF → proofOfLifeUrl embedded in GET /admin/validation (legacy).
 *
 * Once the rollout finishes, the legacy branch and the validation field are removed.
 */
export const useProofOfLifeUrl = ({
  enabled = true,
}: UseProofOfLifeUrlOptions = {}) => {
  const { enabled: endpointEnabled, loading: flagLoading } = useFeatureFlag(
    FeatureFlag.FS_COMMONS_PROOF_OF_LIFE_URL_ENDPOINT_ENABLED,
  );

  const identityValidation = useIdentityValidation({
    enabled: enabled && !endpointEnabled,
  });

  const proofOfLifeUrlQuery = useQuery({
    queryKey: ['proofOfLifeUrl'],
    queryFn: () => configurationService.getProofOfLifeUrl(),
    enabled: enabled && endpointEnabled,
    // The dedicated endpoint returns 500 when dLocal has no url yet, which is
    // often transient. Retry 3 times with exponential backoff before giving up.
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  });

  if (endpointEnabled) {
    return {
      proofOfLifeUrl: proofOfLifeUrlQuery.data?.proofOfLifeUrl,
      isLoading: flagLoading || proofOfLifeUrlQuery.isInitialLoading,
      isError: proofOfLifeUrlQuery.isError,
    };
  }

  return {
    proofOfLifeUrl: identityValidation.data?.proofOfLifeUrl,
    isLoading: flagLoading || identityValidation.isInitialLoading,
    isError: identityValidation.isError,
  };
};
